@file:OptIn(ExperimentalJsExport::class)

package com.dotpx.ambient

import bio.aq.audio.AudioPlayer
import bio.aq.audio.PcmSource
import bio.aq.audio.renderChannels
import kotlin.random.Random

/** Shared controller and PCM renderer; instantiated only inside the audio worklet. */
@JsExport
class WebAmbientEngine {
    private lateinit var player: AudioPlayer
    private val controller = AmbientController(
        dwellSeconds = 30,
        morphSeconds = 30,
        nowMillis = { ((js("globalThis.currentTime") as Double) * 1_000.0).toLong() },
        nextSeed = { Random.nextLong().toULong() },
        nextSceneIndex = { bound -> Random.nextInt(bound) },
        outputFactory = { render ->
            player = AudioPlayer(PcmSource(render))
            object : ControllerAudioOutput {
                override fun start() = player.play()
                override fun pause() = player.pause()
                override fun stop() = player.pause()
                override fun consumeFailure(): Throwable? = player.takeFailure()
                override fun release() = player.close()
            }
        },
    )

    fun renderChannels(left: dynamic, right: dynamic) = player.renderChannels(left, right)

    fun toggle() {
        controller.togglePlayback()
    }

    fun playPreset(sceneIndex: Int) {
        val scene = AmbienceScene.entries.getOrNull(sceneIndex) ?: return
        controller.playPreset(scene)
    }

    fun shuffle() {
        controller.shuffle()
    }

    fun pause() {
        controller.pause()
    }

    fun setPremium(isPremium: Boolean) {
        controller.configurePlaybackAccess(isPremium = isPremium, requestedLimitMinutes = 0)
    }

    fun tick() {
        controller.tick()
    }

    fun phase(): Int = controller.currentState.playback.phase.ordinal

    fun sceneIndex(): Int = controller.currentState.playback.scene?.ordinal ?: -1

    fun sceneKey(): String = controller.currentState.playback.scene?.name ?: ""

    fun sceneTitle(): String = controller.currentState.playback.scene?.title ?: ""

    fun sceneCount(): Int = AmbienceScene.entries.size

    fun sceneTitleAt(sceneIndex: Int): String = AmbienceScene.entries.getOrNull(sceneIndex)?.title ?: ""

    fun secondsUntilAdvance(): Int = controller.currentState.playback.secondsUntilAdvance ?: -1

    fun errorMessage(): String = when (controller.currentState.playback.errorMessage) {
        null -> ""

        "オーディオ機能が再起動されました。もう一度再生してください。" ->
            "Audio restarted. Please start playback again."

        else -> "Audio playback failed. Please try again."
    }

    fun visualGeneration(): Double = controller.currentState.visualization.baseTransitionGeneration.toDouble()

    fun visualSignals(): dynamic {
        val result: dynamic = js("[]")
        for (signal in controller.currentState.visualization.lightSignals) {
            val item: dynamic = js("({})")
            item.sound = signal.sound.ordinal
            item.weight = signal.weight
            item.convergence = signal.convergence
            item.energy = signal.energy
            result.push(item)
        }
        return result
    }

    fun dispose() {
        controller.release()
    }
}

// The UMD export is loaded by audio-worklet.js; no window or DOM dependencies.
fun main() = Unit
